"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Decimal = exports.Float = void 0;
const FromJsConstructor_1 = require("./FromJsConstructor");
const Value_1 = require("./Value");
const IonDecimal_1 = require("../IonDecimal");
const IonTypes_1 = require("../IonTypes");
/**
 * Represents a float[1] value in an Ion stream.
 *
 * [1] http://amzn.github.io/ion-docs/docs/spec.html#float
 */
class Float extends (0, Value_1.Value)(Number, IonTypes_1.IonTypes.FLOAT, new FromJsConstructor_1.FromJsConstructorBuilder()
    .withPrimitives(FromJsConstructor_1.Primitives.Number)
    .withClassesToUnbox(Number)
    .build()) {
    /**
     * Constructor.
     * @param value         The numeric value to represent as a float.
     * @param annotations   An optional array of strings to associate with `value`.
     */
    constructor(value, annotations = []) {
        super(value);
        this._setAnnotations(annotations);
    }
    numberValue() {
        return +this.valueOf();
    }
    writeTo(writer) {
        writer.setAnnotations(this.getAnnotations());
        writer.writeFloat64(this.numberValue());
    }
    _valueEquals(other, options = {
        epsilon: null,
        ignoreAnnotations: false,
        ignoreTimestampPrecision: false,
        onlyCompareIon: true,
        coerceNumericType: false,
    }) {
        let isSupportedType = false;
        let valueToCompare = null;
        // if the provided value is an ion.dom.Float instance.
        if (other instanceof Float) {
            isSupportedType = true;
            valueToCompare = other.numberValue();
        }
        else if (options.coerceNumericType === true && other instanceof Decimal) {
            // if other is Decimal convert both values to Decimal for comparison.
            let thisValue = new IonDecimal_1.Decimal(other.toString());
            return thisValue.equals(other.decimalValue());
        }
        else if (!options.onlyCompareIon) {
            // We will consider other Float-ish types
            if (other instanceof Number || typeof other === "number") {
                isSupportedType = true;
                valueToCompare = other.valueOf();
            }
        }
        if (!isSupportedType) {
            return false;
        }
        let result = Object.is(this.numberValue(), valueToCompare);
        if (options.epsilon != null) {
            if (result ||
                Math.abs(this.numberValue() - valueToCompare) <= options.epsilon) {
                return true;
            }
        }
        return result;
    }
}
exports.Float = Float;
/**
 * Represents a decimal[1] value in an Ion stream.
 *
 * [1] http://amzn.github.io/ion-docs/docs/spec.html#decimal
 */
class Decimal extends (0, Value_1.Value)(Number, IonTypes_1.IonTypes.DECIMAL, new FromJsConstructor_1.FromJsConstructorBuilder()
    .withClasses(IonDecimal_1.Decimal)
    .build()) {
    /**
     * Constructor.
     * @param value         The numeric value to represent as a decimal.
     * @param annotations   An optional array of strings to associate with `value`.
     */
    constructor(value, annotations = []) {
        super(value.numberValue());
        this._decimalValue = value;
        this._numberValue = value.numberValue();
        this._setAnnotations(annotations);
    }
    numberValue() {
        return this._numberValue;
    }
    decimalValue() {
        return this._decimalValue;
    }
    toString() {
        return this._decimalValue.toString();
    }
    valueOf() {
        return this._numberValue;
    }
    writeTo(writer) {
        writer.setAnnotations(this.getAnnotations());
        writer.writeDecimal(this.decimalValue());
    }
    _valueEquals(other, options = {
        epsilon: null,
        ignoreAnnotations: false,
        ignoreTimestampPrecision: false,
        onlyCompareIon: true,
        coerceNumericType: false,
    }) {
        let isSupportedType = false;
        let valueToCompare = null;
        // if the provided value is an ion.dom.Decimal instance.
        if (other instanceof Decimal) {
            isSupportedType = true;
            valueToCompare = other.decimalValue();
        }
        else if (options.coerceNumericType === true && other instanceof Float) {
            isSupportedType = true;
            valueToCompare = new IonDecimal_1.Decimal(other.toString());
        }
        else if (!options.onlyCompareIon) {
            // We will consider other Decimal-ish types
            if (other instanceof IonDecimal_1.Decimal) {
                // expectedValue is a non-DOM Decimal
                isSupportedType = true;
                valueToCompare = other;
            }
            else if (other instanceof Number || typeof other === "number") {
                isSupportedType = true;
                // calling numberValue() on IonDecimal is lossy and could result in imprecise comparisons
                // hence converting number to IonDecimal for comparison even though it maybe expensive
                valueToCompare = new IonDecimal_1.Decimal(other.toString());
            }
        }
        if (!isSupportedType) {
            return false;
        }
        return this.decimalValue().equals(valueToCompare);
    }
}
exports.Decimal = Decimal;
